/*
  <product-tabs> (chunk 04): the tabs of sections/product-tabs.liquid.

  WAI-ARIA tabs with automatic activation: Left/Right arrows move between tabs and wrap,
  Home and End jump to the first and last tab, and the focused tab is selected. Only the
  selected tab is in the Tab order (roving tabindex).

  The Pinout panel's iframe carries its URL in data-src and gets src the first time the tab
  is selected, so the pinout page never loads with the product page.

  Links anywhere on the page with data-open-tab="<name>" (the Interactive pinout tile of the
  documentation box) open that tab and scroll to it instead of navigating, so they lose the
  "Opens in a new window" description they carry for the plain-link case. When this section
  has no such tab, the link works as a plain link and keeps it.

  A URL hash of #overview, #specs, #pinout or #reviews opens that tab and scrolls to it, on
  load and when the hash changes. On load the tabs stay in view while the page settles (the
  complementary products above them can arrive after the load event), until the visitor
  scrolls, clicks or presses a key, or five seconds after the load event. Focus doesn't move.
*/
if (!customElements.get('product-tabs')) {
  customElements.define(
    'product-tabs',
    class ProductTabs extends HTMLElement {
      static NEW_WINDOW_MESSAGE = 'a11y-new-window-message';
      static SETTLE_TIME = 5000;

      constructor() {
        super();
        this.onKeydown = this.onKeydown.bind(this);
        this.onTabClick = this.onTabClick.bind(this);
        this.onDocumentClick = this.onDocumentClick.bind(this);
        this.onHashChange = this.onHashChange.bind(this);
      }

      connectedCallback() {
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));
        this.panels = Array.from(this.querySelectorAll('[data-panel]'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', this.onTabClick);
          tab.addEventListener('keydown', this.onKeydown);
        });
        document.addEventListener('click', this.onDocumentClick);
        window.addEventListener('hashchange', this.onHashChange);
        this.quietOpeners();

        // A lone pinout panel has src from the start (lazy loaded); hide its loading line on load.
        this.querySelectorAll('.product-tabs__frame[src]').forEach((frame) => this.watchFrame(frame));

        // A visitor who has already scrolled (or a reload that restored the position) stays put.
        if (this.openFromHash() && window.scrollY === 0) this.revealWhileLoading();
      }

      disconnectedCallback() {
        document.removeEventListener('click', this.onDocumentClick);
        window.removeEventListener('hashchange', this.onHashChange);
        this.stopPinning?.();
        this.restoreOpeners();
      }

      tabFor(name) {
        return this.tabs.find((tab) => tab.dataset.tab === name) || null;
      }

      panelFor(name) {
        return this.panels.find((panel) => panel.dataset.panel === name) || null;
      }

      select(name, { focus = false } = {}) {
        const tab = this.tabFor(name);
        if (!tab) return false;

        this.tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;
        });
        this.panels.forEach((panel) => {
          panel.hidden = panel.dataset.panel !== name;
        });

        const frame = this.panelFor(name)?.querySelector('iframe[data-src]');
        if (frame && !frame.getAttribute('src')) {
          this.watchFrame(frame);
          frame.setAttribute('src', frame.dataset.src);
        }

        if (focus) tab.focus();
        return true;
      }

      watchFrame(frame) {
        frame.addEventListener(
          'load',
          () => {
            frame.closest('.product-tabs__frame-wrap')?.setAttribute('data-loaded', '');
          },
          { once: true }
        );
      }

      // Openers this section handles open a tab here, not a new window, so they drop the
      // new-window description that snippets/product-docs.liquid gives them. Other ids in
      // aria-describedby stay; disconnectedCallback gives it back (theme editor).
      quietOpeners() {
        this.quietedOpeners = [];
        document.querySelectorAll('[data-open-tab]').forEach((opener) => {
          if (this.contains(opener) || !this.panelFor(opener.dataset.openTab)) return;
          const ids = (opener.getAttribute('aria-describedby') || '').split(/\s+/);
          if (!ids.includes(ProductTabs.NEW_WINDOW_MESSAGE)) return;
          this.setDescribedBy(opener, ids.filter((id) => id !== ProductTabs.NEW_WINDOW_MESSAGE));
          this.quietedOpeners.push(opener);
        });
      }

      restoreOpeners() {
        this.quietedOpeners?.forEach((opener) => {
          const ids = (opener.getAttribute('aria-describedby') || '').split(/\s+/);
          if (!ids.includes(ProductTabs.NEW_WINDOW_MESSAGE)) {
            this.setDescribedBy(opener, [...ids, ProductTabs.NEW_WINDOW_MESSAGE]);
          }
        });
        this.quietedOpeners = [];
      }

      setDescribedBy(element, ids) {
        const value = ids.filter(Boolean).join(' ');
        if (value) {
          element.setAttribute('aria-describedby', value);
        } else {
          element.removeAttribute('aria-describedby');
        }
      }

      // Opens the tab the URL hash names. Returns whether this section has that panel; a lone
      // panel has no tab to select but is still scrolled to.
      openFromHash() {
        const name = window.location.hash.slice(1);
        if (!name || !this.panelFor(name)) return false;
        this.select(name);
        return true;
      }

      onHashChange() {
        if (this.openFromHash()) this.reveal();
      }

      // Puts this section under the sticky header (scroll-margin-top in
      // component-product-v2.css) at once, without moving focus. The scroll can switch the
      // header to its compact state, which lowers --header-visible-height and so the margin;
      // the header handles that scroll event before the next frame's callbacks, so a second
      // pass then lands under the header as it is.
      reveal() {
        const align = () => {
          this.scrollIntoView({ behavior: 'auto', block: 'start' });
          this.revealedY = window.scrollY;
        };
        align();
        requestAnimationFrame(align);
      }

      // For a deep link: reveal now, and again whenever the page changes size until it settles,
      // because content above the tabs (the complementary products) can still arrive. Ends on
      // the visitor's first wheel, pointer or key input, on any scroll that isn't ours (find in
      // page, a screen reader, a restored position) or SETTLE_TIME after the load event.
      revealWhileLoading() {
        const inputs = ['wheel', 'pointerdown', 'keydown'];
        const observer = new ResizeObserver(() => this.reveal());
        let timer = null;
        const onScroll = () => {
          if (Math.abs(window.scrollY - this.revealedY) > 1) stop();
        };
        // A page loaded in a background tab only fetches its lazy content once it is shown, so
        // the settle time starts then.
        const startTimer = () => {
          if (document.hidden) {
            document.addEventListener('visibilitychange', startTimer, { once: true });
          } else {
            timer = setTimeout(stop, ProductTabs.SETTLE_TIME);
          }
        };
        const stop = () => {
          observer.disconnect();
          clearTimeout(timer);
          window.removeEventListener('load', startTimer);
          document.removeEventListener('visibilitychange', startTimer);
          window.removeEventListener('scroll', onScroll);
          inputs.forEach((type) => window.removeEventListener(type, stop, true));
          if (this.stopPinning === stop) this.stopPinning = null;
        };
        this.stopPinning = stop;

        this.reveal();
        observer.observe(document.body);
        window.addEventListener('scroll', onScroll, { passive: true });
        inputs.forEach((type) => window.addEventListener(type, stop, { capture: true, passive: true }));
        if (document.readyState === 'complete') {
          startTimer();
        } else {
          window.addEventListener('load', startTimer, { once: true });
        }
      }

      onTabClick(event) {
        this.select(event.currentTarget.dataset.tab);
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        let next = null;

        switch (event.key) {
          case 'ArrowRight':
            next = (index + 1) % this.tabs.length;
            break;
          case 'ArrowLeft':
            next = (index - 1 + this.tabs.length) % this.tabs.length;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = this.tabs.length - 1;
            break;
          default:
            return;
        }

        event.preventDefault();
        this.select(this.tabs[next].dataset.tab, { focus: true });
      }

      onDocumentClick(event) {
        const opener = event.target.closest('[data-open-tab]');
        if (!opener || this.contains(opener)) return;

        const name = opener.dataset.openTab;
        const panel = this.panelFor(name);
        if (!panel) return;
        // Let modified clicks open the link itself in a new tab or window.
        if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

        event.preventDefault();
        const tab = this.tabFor(name);
        if (tab) this.select(name);

        const smooth = !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto', block: 'start' });
        (tab || panel).focus({ preventScroll: true });
      }
    }
  );
}
